import { Product } from '@shared/components/ui/Product';
import { RenderItemProps } from '@shared/components/ui/VirtualList';
import { useTranslation } from 'react-i18next';
import { LocationMovementsModel } from '../../models/LocationMovements';
import { Badge } from '@shared/components/ui/badge';
import { UserCard } from '@shared/components/ui/usercard';
import { MovementBatchCell } from '@modules/Reports/components/MovementBatchCell';
import { renderMovementReferences } from './renderShopFromEvent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faLocationXmark } from '@fortawesome/pro-regular-svg-icons';

// 🎯 Helper: formatea la fecha en formato DD/MM/AAAA H:M
const formatDateTime = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '-';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

// 🎯 Props para Card incluyendo las acciones
export interface CardProps extends RenderItemProps<LocationMovementsModel> {
  className?: string;
  showActions?: boolean;
  showIndex?: boolean;
}

// 🎯 Componente de card individual para Location - SRP
export const Card = ({ item: stockmovements, className = '' }: CardProps) => {
  const { t } = useTranslation('locationmovementsModule');

  const eventKey = stockmovements.event?.name ?? String(stockmovements.event?.id ?? '');
  const eventName = t(`filters.options.${eventKey}`, {
    defaultValue: eventKey || '-',
  }).toString();

  const formatted = formatDateTime(stockmovements.created_at ?? undefined);

  return (
    <div className={`py-4 px-3 border-b border-lines hover:bg-muted/30 transition-colors ${className}`}>
      <div className="flex flex-col gap-1">
        <div className="border-b border-card-border pb-3">
          <Product
            src={stockmovements.product.image || ''}
            name={stockmovements.product.name || stockmovements.product.code || ''}
            code={stockmovements.product.code}
            id={stockmovements.product.id}
            enableLineClamp={false}
            showTooltip={true}
            showImageModal={true}
          />
          <div className="pt-2 pb-1">
            <MovementBatchCell item={stockmovements} i18nNs="locationmovementsModule" />
          </div>
          <div className="flex items-center gap-2 flex-wrap pt-2">
            <Badge variant="outline">{`Cantidad - ${stockmovements.qty}`}</Badge>
            <Badge variant="outline" className="text-text">
              {`Total ${stockmovements.total ?? 0}`}
            </Badge>
          </div>
        </div>
        <div className="flex items-center gap-2 px-1 py-3 border-b border-card-border">
          <span className="text-sm text-title min-w-[40px] uppercase">
            {stockmovements.from_location?.code || (
              <FontAwesomeIcon icon={faLocationXmark} className="w-5 h-5 opacity-65" />
            )}
          </span>
          <div className="h-px flex-1 border-t border-dashed border-title opacity-65" />
          <div className="text-muted-foreground rounded-full border border-muted w-6 h-6 flex items-center justify-center text-xs">
            <FontAwesomeIcon icon={faArrowRight} className="w-3" />
          </div>
          <div className="h-px flex-1 border-t border-dashed border-title opacity-65" />
          <span className="text-sm text-title min-w-[40px] uppercase text-right">
            {stockmovements.to_location?.code || (
              <FontAwesomeIcon icon={faLocationXmark} className="w-5 h-5  opacity-65" />
            )}
          </span>
        </div>
        <div className="flex items-start justify-between gap-3 pb-1">
          <div className="min-w-0 flex-1">{renderMovementReferences(stockmovements)}</div>
          <div className="shrink-0 text-sm">{eventName}</div>
        </div>
        <div className="flex items-center justify-between ">
          {stockmovements.user?.name ? (
            <UserCard
              name={stockmovements.user.name}
              email={stockmovements.user.email ?? ''}
              avatarUrl={stockmovements.user.image}
              data-testid={`user-${stockmovements.id}`}
            />
          ) : (
            <span className="text-sm text-complementary-text">{t('fallbacks.no_user')}</span>
          )}
          <span className="text-sm">{formatted}</span>
        </div>
      </div>
    </div>
  );
};
